import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { arrProxy, changeNestedArrKeyValueOnCondition } from "core/iterable_utils";
import { USER_SETTINGS_KEY } from "core/local_storage_utils";
import { parseJsonString } from "core/str_utils";

export const fetchCcpApprovalPeriods = createAsyncThunk("fsnb/fetchCcpApprovalPeriods", async (_, { rejectWithValue, extra }) => {
	try {
		const { data } = await extra.apiFsnb.getCcpMosRegionApprovalPeriods();
		if (!data) {
			throw new Error();
		}
		return data;
	} catch (e) {
		return rejectWithValue(e);
	}
});

const initialState = () => {
	const userSettings = parseJsonString(localStorage.getItem(USER_SETTINGS_KEY));

	return {
		startSearchObj: null,
		startSearchCode: "",
		treeExpanded: userSettings?.fsnb?.treeExpanded ?? true,
		treeWidth: userSettings?.fsnb?.treeWidth ?? 350,
		estimateFsnbTreeExpanded: userSettings?.fsnb?.estimateFsnbTreeExpanded ?? true,
		estimateFsnbTreeWidth: userSettings?.fsnb?.estimateFsnbTreeWidth ?? 350,
		tableData: [],
		ccpApprovalPeriods: [],
	};
};

export const fsnbSlice = createSlice({
	name: "fsnb",
	initialState,
	reducers: {
		setStartSearchObj: (state, { payload }) => {
			state.startSearchObj = payload;
		},
		setStartSearchCode: (state, { payload }) => {
			state.startSearchCode = payload;
		},
		setTreeExpanded: (state, { payload }) => {
			state.treeExpanded = payload;
		},
		setTreeWidth: (state, { payload }) => {
			state.treeWidth = payload;
		},
		setEstimateFsnbTreeExpanded: (state, { payload }) => {
			state.estimateFsnbTreeExpanded = payload;
		},
		setEstimateFsnbTreeWidth: (state, { payload }) => {
			state.estimateFsnbTreeWidth = payload;
		},
		setTableData: (state, { payload }) => {
			state.tableData = payload;
		},
		setCcpApprovalPeriods: (state, { payload }) => {
			state.ccpApprovalPeriods = payload;
		},
		changeNormInnerContentVisible: (state, { payload }) => {
			const { clickedRow, contentType } = payload;
			state.tableData = changeNestedArrKeyValueOnCondition({
				arr: state.tableData,
				keySearch: "id",
				valSearch: clickedRow?.id,
				callback: (row) => {
					if (contentType === "content") {
						row.contentVisibility = !row.contentVisibility;
					} else if (contentType === "resources") {
						row.resourcesVisibility = !row.resourcesVisibility;
					}
					if (row.contentVisibility && row.resourcesVisibility) {
						row._children = [...arrProxy(row.norm_content), ...arrProxy(row.norm_resource)];
					} else if (row.contentVisibility && !row.resourcesVisibility) {
						row._children = [...arrProxy(row.norm_content)];
					} else if (!row.contentVisibility && row.resourcesVisibility) {
						row._children = [...arrProxy(row.norm_resource)];
					} else if (!row.contentVisibility && !row.resourcesVisibility) {
						row._children = [];
					}
					return row;
				},
			});
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchCcpApprovalPeriods.fulfilled, (state, { payload }) => {
			state.ccpApprovalPeriods = payload;
		});
	},
});

export const { setStartSearchObj, setStartSearchCode, setTableData, changeNormInnerContentVisible } = fsnbSlice.actions;
export default fsnbSlice.reducer;

export const fsnbActions = fsnbSlice.actions;

export const getFsnbTreeExpanded = (state) => state.fsnb.treeExpanded;
export const getFsnbTreeWidth = (state) => state.fsnb.treeWidth;
export const getEstimateFsnbTreeExpanded = (state) => state.fsnb.estimateFsnbTreeExpanded;
export const getEstimateFsnbTreeWidth = (state) => state.fsnb.estimateFsnbTreeWidth;
export const getTableData = (state) => state.fsnb.tableData;
export const getFsnbCcpApprovalPeriods = (state) => state.fsnb.ccpApprovalPeriods;
